/**
 * @element qds-dialog
 * A native modal dialog with slotted content, composed-tree focus, and async actions.
 * Nonmodal Escape yields to inner controls/overlays and ignores IME composition.
 * @attr {boolean} open - Whether the dialog is open.
 * @attr {string} label - Title fallback and accessible name when no title is slotted.
 * @attr {boolean} nonmodal - Use a nonmodal dialog; default is modal.
 * @attr {boolean} no-escape - Prevent Escape dismissal.
 * @attr {boolean} close-on-backdrop - Allow clicking the backdrop to request closure.
 * @attr {boolean} hide-close - Hide the header close button; provide a footer action.
 * @attr {boolean} busy - Mark an action in progress and prevent user dismissal.
 * @attr {'body'|'dialog'} scroll - Scroll content only (default) or the whole dialog.
 * @property {boolean} open - Reflects the open attribute.
 * @property {boolean} busy - Reflects async progress.
 * @property {HTMLElement|null} initialFocus - Optional initial focus target.
 * @property {HTMLElement|null} returnFocus - Optional logical focus destination.
 * @property {string} returnValue - Last close result.
 * @slot title - Title content, replacing the label fallback.
 * @slot - Main dialog content.
 * @slot body - Alternative named slot for main content.
 * @slot footer - Action controls beneath the body.
 * @fires open - The dialog entered the top layer.
 * @fires before-close - Cancelable close request with reason and returnValue.
 * @fires close - Dialog closed; detail includes reason and returnValue.
 * @fires action-error - An async action failed; detail includes error.
 * @csspart dialog - Native dialog surface.
 * @csspart title - Title container.
 * @csspart body - Scrollable content.
 * @csspart footer - Footer actions.
 * @csspart close - Header close button.
 */
import { loadBaseCSS } from '../shared/button-utils.js';
import { overlayActiveElement, overlayContains, overlayFocusable, lockOverlayScroll } from '../shared/overlay-utils.js';

export type DialogCloseReason = 'api' | 'escape' | 'backdrop' | 'close-button' | 'action' | 'attribute' | 'native' | 'disconnect';
const modalDialogs: QdsDialog[] = [];
export class QdsDialog extends HTMLElement {
  static get observedAttributes(): string[] { return ['open', 'label', 'busy', 'nonmodal', 'hide-close', 'scroll']; }
  private _initialFocus: HTMLElement | null = null;
  private _returnFocus: HTMLElement | null = null;
  returnValue = '';
  private _dialog: HTMLDialogElement;
  private _invoker: HTMLElement | null = null;
  private _releaseScroll: (() => void) | null = null;
  private _shown = false;
  private _reflecting = false;
  private _action: AbortController | null = null;
  private _backdropDown = false;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.innerHTML = `<style>
      :host { display: contents; color: var(--theme-text-primary, #333); font-family: var(--font-family-primary, 'IBM Plex Sans', sans-serif); }
      dialog { box-sizing: border-box; width: var(--qds-dialog-width, 36rem); max-width: calc(100vw - 2rem); max-height: calc(100dvh - 2rem); margin: auto; padding: 0; border: 1px solid var(--theme-border-color, #e0e0e0); border-radius: var(--qds-dialog-radius, 12px); background: var(--theme-background-primary, #fff); color: inherit; box-shadow: var(--shadow-medium, 0 2px 10px rgba(0,0,0,.1)); overflow: auto; }
      dialog[open] { display: flex; flex-direction: column; }
      dialog::backdrop { background: var(--qds-dialog-backdrop, rgba(0,0,0,.45)); }
      .header { display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 1rem 1.5rem; flex: 0 0 auto; }
      h2 { margin: 0; font: inherit; font-size: 1.25rem; font-weight: 600; }
      .body { min-height: 0; overflow: auto; overscroll-behavior: contain; padding: 0 1.5rem 1.5rem; }
      :host([scroll="dialog"]) .body { overflow: visible; }
      .footer { padding: 1rem 1.5rem; border-top: 1px solid var(--theme-border-color, #e0e0e0); flex: 0 0 auto; }
      .footer[hidden], [hidden] { display: none !important; }
      button { display: inline-grid; place-items: center; min-width: 44px; min-height: 44px; border: 0; border-radius: 4px; background: transparent; color: inherit; font: inherit; cursor: pointer; }
      button:hover { background: var(--theme-background-hover, rgba(0,0,0,.05)); }
      button:focus-visible, h2:focus-visible { outline: 2px solid var(--theme-accent-primary, #4285f4); outline-offset: 2px; }
      button:disabled { opacity: .55; cursor: default; }
      .error { color: var(--theme-text-primary, #333); background: var(--theme-background-primary, #fff); border-inline-start: 3px solid var(--theme-error, #8b1f00); padding: .5rem .75rem; margin: 0 1.5rem 1rem; }
      @media (prefers-reduced-motion: reduce) { * { scroll-behavior: auto; } }
    </style><dialog part="dialog" aria-labelledby="dialog-title"><header class="header"><h2 id="dialog-title" part="title" tabindex="-1"><slot name="title"><span id="title-fallback"></span></slot></h2><button type="button" part="close" aria-label="Close dialog">×</button></header><div class="body" part="body"><slot></slot><slot name="body"></slot></div><p class="error" role="alert" hidden></p><footer class="footer" part="footer" hidden><slot name="footer"></slot></footer></dialog>`;
    this._dialog = this.shadowRoot!.querySelector('dialog')!;
    this._dialog.addEventListener('cancel', event => { event.preventDefault(); this.requestClose('escape'); });
    this._dialog.addEventListener('close', event => {
      // Nested QDS close events bubble; queued native events may arrive after a reopen.
      if (event.target === this._dialog && this._shown && !this._dialog.open) this._finishClose(this._dialog.returnValue, 'native');
    });
    this.shadowRoot!.querySelector('button')!.addEventListener('click', () => this.requestClose('close-button'));
    this._dialog.addEventListener('pointerdown', event => { this._backdropDown = this._isBackdrop(event); });
    this._dialog.addEventListener('click', event => {
      if (this._backdropDown && this._isBackdrop(event) && this.hasAttribute('close-on-backdrop')) this.requestClose('backdrop');
      this._backdropDown = false;
    });
    this.shadowRoot!.querySelector<HTMLSlotElement>('slot[name="footer"]')!.addEventListener('slotchange', () => this._sync());
  }
  get open(): boolean { return this.hasAttribute('open'); }
  set open(value: boolean) { this.toggleAttribute('open', Boolean(value)); }
  get busy(): boolean { return this.hasAttribute('busy'); }
  set busy(value: boolean) { this.toggleAttribute('busy', Boolean(value)); }
  get initialFocus(): HTMLElement | null { return this._initialFocus; }
  set initialFocus(value: HTMLElement | null) { this._initialFocus = value; }
  get returnFocus(): HTMLElement | null { return this._returnFocus; }
  set returnFocus(value: HTMLElement | null) { this._returnFocus = value; }
  connectedCallback(): void {
    loadBaseCSS();
    for (const property of ['open', 'busy', 'initialFocus', 'returnFocus']) {
      if (Object.prototype.hasOwnProperty.call(this, property)) {
        const value = (this as any)[property]; delete (this as any)[property]; (this as any)[property] = value;
      }
    }
    this._sync();
    if (this.open) this._show();
  }
  disconnectedCallback(): void { if (this._shown) this.close('', 'disconnect'); }
  attributeChangedCallback(name: string): void {
    this._sync();
    if (!this.isConnected || this._reflecting) return;
    if (name === 'open') { if (this.open) this._show(); else this.close('', 'attribute'); }
    if (name === 'nonmodal' && this._shown) { this.close('', 'attribute'); this.show(); }
  }
  /** Opens a modal (or nonmodal) dialog and remembers its logical invoker. */
  show(invoker?: HTMLElement): void {
    if (invoker) this._invoker = invoker;
    this.open = true;
    if (this.isConnected && !this._shown) this._show();
  }
  /** Closes programmatically; use requestClose for a cancelable user request. */
  close(value = '', reason: DialogCloseReason = 'api'): void {
    if (!this._shown) { this._reflectOpen(false); return; }
    if (this._dialog.open && typeof this._dialog.close === 'function') this._dialog.close(value);
    else this._dialog.removeAttribute('open');
    this._finishClose(value, reason);
  }
  requestClose(reason: DialogCloseReason = 'api', value = ''): boolean {
    if (!this._shown || this.busy || (reason === 'escape' && this.hasAttribute('no-escape'))) return false;
    if (!this.dispatchEvent(new CustomEvent('before-close', { bubbles: true, composed: true, cancelable: true, detail: { reason, returnValue: value } }))) return false;
    this.close(value, reason); return true;
  }
  /** Keeps the dialog open on false/error; aborts its signal if closed or removed. */
  async runAction(action: (signal: AbortSignal) => unknown | Promise<unknown>, value = 'confirm'): Promise<boolean> {
    if (this.busy || !this._shown) return false;
    const controller = new AbortController(); this._action = controller; this.busy = true;
    const errorMessage = this.shadowRoot!.querySelector<HTMLElement>('.error')!;
    errorMessage.hidden = true; errorMessage.textContent = '';
    try {
      const result = await action(controller.signal);
      if (controller.signal.aborted || !this._shown || result === false) return false;
      this.close(value, 'action'); return true;
    } catch (error) {
      if (!controller.signal.aborted) {
        errorMessage.textContent = error instanceof Error ? error.message : 'The action could not be completed.';
        errorMessage.hidden = false;
        this.dispatchEvent(new CustomEvent('action-error', { detail: { error }, bubbles: true, composed: true }));
      }
      return false;
    } finally {
      if (this._action === controller) { this._action = null; this.busy = false; }
    }
  }
  private _reflectOpen(value: boolean): void { this._reflecting = true; this.open = value; this._reflecting = false; }
  private _show(): void {
    if (this._shown) return;
    this._invoker ||= overlayActiveElement(this.ownerDocument);
    this.returnValue = '';
    this._shown = true;
    const modal = !this.hasAttribute('nonmodal');
    this._dialog.setAttribute('aria-modal', String(modal));
    if (modal && typeof this._dialog.showModal === 'function') this._dialog.showModal();
    else if (typeof this._dialog.show === 'function') this._dialog.show();
    else this._dialog.setAttribute('open', '');
    if (modal) { this._releaseScroll = lockOverlayScroll(this.ownerDocument); modalDialogs.push(this); }
    this.ownerDocument.addEventListener('keydown', this._handleKeyDown, true);
    this.ownerDocument.addEventListener('keydown', this._handleEscape);
    const autofocus = [...this.querySelectorAll<HTMLElement>('[autofocus]')].find(element => !element.hasAttribute('disabled'));
    const target = this.initialFocus || autofocus || this.shadowRoot!.querySelector<HTMLElement>('h2');
    target?.focus({ preventScroll: true });
    this.dispatchEvent(new CustomEvent('open', { bubbles: true, composed: true }));
  }
  private _finishClose(value: string, reason: DialogCloseReason): void {
    if (!this._shown) return;
    this._shown = false; this._reflectOpen(false); this.returnValue = value;
    this._action?.abort(); this._action = null; this.busy = false;
    this._releaseScroll?.(); this._releaseScroll = null;
    const index = modalDialogs.indexOf(this);
    if (index >= 0) modalDialogs.splice(index, 1);
    this.ownerDocument.removeEventListener('keydown', this._handleKeyDown, true);
    this.ownerDocument.removeEventListener('keydown', this._handleEscape);
    const target = this.returnFocus || this._invoker;
    this._invoker = null;
    if (target?.isConnected && !target.hasAttribute('disabled') && !target.closest('[inert]')) target.focus({ preventScroll: true });
    this.dispatchEvent(new CustomEvent('close', { detail: { reason, returnValue: value }, bubbles: true, composed: true }));
  }
  private _handleKeyDown = (event: KeyboardEvent): void => {
    if (!this._shown || this.hasAttribute('nonmodal') || event.key !== 'Tab' || modalDialogs[modalDialogs.length - 1] !== this) return;
    const active = overlayActiveElement(this.ownerDocument);
    // A nested native modal owns its own tab sequence.
    const nested = active?.closest('dialog');
    if (nested && nested !== this._dialog && !overlayContains(this._dialog, nested)) return;
    const focusable = overlayFocusable(this._dialog);
    if (!focusable.length) { event.preventDefault(); this.shadowRoot!.querySelector<HTMLElement>('h2')!.focus(); return; }
    const index = focusable.indexOf(active!);
    if (index < 0 || (event.shiftKey ? index === 0 : index === focusable.length - 1)) {
      event.preventDefault(); (event.shiftKey ? focusable[focusable.length - 1] : focusable[0]).focus();
    }
  };
  private _handleEscape = (event: KeyboardEvent): void => {
    if (event.defaultPrevented || event.isComposing || event.key !== 'Escape' || !this._shown || !this.hasAttribute('nonmodal')) return;
    // Inner anchored overlays handle Escape during capture. At bubble time,
    // an inner QDS/native dialog owns its own dismissal and composition.
    const path = event.composedPath();
    const owner = path.find(node => node instanceof QdsDialog);
    if (owner && owner !== this) return;
    const nativeOwner = path.find(node => node instanceof HTMLElement && node.tagName === 'DIALOG');
    if (nativeOwner && nativeOwner !== this._dialog) return;
    if (overlayContains(this, overlayActiveElement(this.ownerDocument))) { event.preventDefault(); this.requestClose('escape'); }
  };
  private _isBackdrop(event: MouseEvent): boolean {
    if (event.target !== this._dialog) return false;
    const rect = this._dialog.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
  }
  private _sync(): void {
    if (!this.shadowRoot) return;
    this.shadowRoot.querySelector('#title-fallback')!.textContent = this.getAttribute('label') || 'Dialog';
    const button = this.shadowRoot.querySelector<HTMLButtonElement>('button')!;
    button.hidden = this.hasAttribute('hide-close'); button.disabled = this.busy;
    this._dialog?.setAttribute('aria-busy', String(this.busy));
    const footer = this.shadowRoot.querySelector<HTMLSlotElement>('slot[name="footer"]')!;
    this.shadowRoot.querySelector<HTMLElement>('footer')!.hidden = !footer.assignedElements().length;
  }
}
if (!customElements.get('qds-dialog')) customElements.define('qds-dialog', QdsDialog);
export default QdsDialog;
